import axios from 'axios';
import log from '../../logger';

/**
 * The one global HTTP behaviour the CMS has, and the reason it cannot be dropped.
 *
 * Angular's `shared/http-interceptor.js` looked like a growl helper. It was not. When a
 * request arrives without a session the server answers **200** with `{redirect: '/login'}`
 * (`server/app/authenticateApi.js:348`), and this is what acts on it. Without a replacement,
 * an expired session would silently do nothing: every screen would sit there with stale data
 * and no explanation. The auth flows use the same mechanism to move the browser on after a
 * successful registration, password reset or change (`authenticateApi.js:110,135,173,193`).
 *
 * **It navigates through the router, not `window.location`.** Angular used `$location.path()`,
 * which stayed inside the SPA. A full page load would work, but it would also discard the
 * success message the auth screen had just set, and it would race that screen's own `navTo`
 * call with a reload rather than with another route change. It would also turn a
 * `{redirect: '/login'}` served *to* the login page into a reload loop, which `$location.path`
 * made structurally impossible because setting the current path is a no-op.
 *
 * **`data.error` is deliberately not handled here.** The Angular interceptor also popped a
 * growl for any response carrying one. Every 200-status `{error}` in the repo is in
 * `authenticateApi.js`, and all five React auth screens already read `data.error` themselves;
 * everything else answers with a real error status, which this never saw. Keeping it would
 * show those failures twice.
 */
export default function installHttpInterceptor(navigate) {
  axios.interceptors.response.use((response) => {
    const redirect = response?.data?.redirect;
    if (typeof redirect !== 'string' || !redirect) return response;

    if (navigate) {
      navigate(redirect);
    } else {
      // Only before the router exists, which in practice means never.
      log.error('A redirect arrived before the router was ready; falling back to a page load.');
      window.location.assign(redirect);
    }

    /*
     * The caller gets a response with no `data`, which is what the Angular interceptor
     * returned (`return {} || $q.when(response)`). It matters: the caller's `.then` runs
     * before the navigation settles, and handing it `{redirect: '/login'}` means a screen
     * doing `data.items.map(...)` throws into the console on the way out.
     */
    return { ...response, data: undefined };
  });
}
